/* eslint-disable no-plusplus */

import * as Sentry from '@sentry/react'
import WallspiceWebgl, { DirectionVectorMap } from 'Renderer/WallspiceWebgl'
import TimeMetric from 'utils/timeMetric'
import GlMemoryRecorder from 'Renderer/GlMemoryRecorder'
import TextureHelper from 'Renderer/TextureHelper'

import * as THREE from 'three'

import {
    loadManifestTexture,
    loadManifestImagePixels,
} from 'Renderer/manifestImageLoader'

import * as WallSnap from 'Renderer/WallSnap'
// import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js'
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js'

import FurnitureManager from 'Renderer/FurnitureManager'
import CustomRenderPass from 'Renderer/CustomRenderPass'
import CustomOutlinePass from 'Renderer/CustomOutlinePass'
import FootprintMaterial from 'Renderer/FootprintMaterial'
import CustomTAARenderPass from 'Renderer/CustomTAARenderPass'

import { computeEraseRegionGridPoints } from 'Renderer/MagicEraserUtils'

import {
    DEFAULT_MOBILE_TEXTURE_SIZE,
    DEFAULT_THREE_NATIVE_LAYER,
    SENTRY_BREADCRUMB_CATEGORY,
    STATIC_STACKABLES_THREE_NATIVE_LAYER,
    AA_FXAA,
    AA_TAA,
} from 'Renderer/constants'

import isMobile from 'utils/isMobile'
import getMaxWindowSize from 'utils/getMaxWindowSize'
import generateThumbnailFromPreview from 'utils/generateThumbnailFromPreview'

import Stats from 'stats.js'
import './style.scss'

const EDGE_THICKNESS_THICK = 0.25
const EDGE_THICKNESS_THIN = 0.25

const EDGE_STRENGTH_THICK = 2.65
const EDGE_STRENGTH_THIN = 2.25

const WGL_GPU_FRAME_TIME_EXT_NAME = 'EXT_disjoint_timer_query_webgl2'

let rendererInstance = null
class Renderer {
    constructor({
        canvas,
        scene: sceneData,
        debugSettings,
        previewSettings: settings,
        onFirstRender,
        onReady,
        saveData,
        onUpdateSaveData,
        onWallDataReady,
        maxTextureSize,
        updateHiddenSurfaces,
        onInitializationFailure,
    }) {
        if (rendererInstance) {
            // eslint-disable-next-line no-console
            console.error('Renderer instance already present.')
            rendererInstance.activeRenderer = false
        }
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        rendererInstance = this

        this.showOutlines = true

        this.furnitureManager = new FurnitureManager()

        // The renderer might get disposed and recreated; so the current active instance is tracked to avoid race conditions
        this.activeRenderer = true

        this.onInitializationFailure = onInitializationFailure
        this.defaultDownloadOptions = { onFail: this.onInitializationFailure }

        this.sceneData = sceneData
        this.manifest = sceneData.manifest
        this.onFirstRender = onFirstRender
        this.onReady = onReady
        this.onUpdateSaveData = onUpdateSaveData
        this.intrinsics = [
            this.manifest.intrinsics_0.m11,
            this.manifest.intrinsics_0.m22,
            this.manifest.intrinsics_0.m31,
            this.manifest.intrinsics_0.m32,
        ]
        this.pointLights = []
        this.directionalLights = []
        this.ambientLight = null

        // eslint-disable-next-line no-constant-condition
        if (this.sceneData.manifest.segmentation_inpainting_0) {
            this.maskDataURL = "segmentation_inpainting_0"
            this.planeOrientation = this.sceneData.manifest.plane_orientation_inpaint_0
            this.maskG = true
        } else if (this.sceneData.manifest.normals_segmentation_inpaint_0) {
            this.maskDataURL = "normals_segmentation_inpaint_0"
            this.planeOrientation = this.sceneData.manifest.plane_orientation_inpaint_0
        } else {
            this.maskDataURL = "normals_segmentation_0"
            this.planeOrientation = this.sceneData.manifest.plane_orientation_0
        }

        this.loadedItems = 0
        this.loadedTotalSize = 0
        this.loadingItems = 0
        this.onWallDataReady = onWallDataReady
        this.doUpdateHiddenSurfaces = updateHiddenSurfaces

        this.additionalDataLoadSize = 0.0

        // Cache reflections for furniture
        this.cubeTex = null

        this.canvas = canvas
        this.wallWidth = 1
        this.wallHeight = 1
        this.camera = WallspiceWebgl.createCamera(this.intrinsics, this.wallWidth, this.wallHeight)

        this.hasWebPSupport = Renderer.checkWebPSupport()

        this.ThreeWGLrenderer = WallspiceWebgl.createRenderer(canvas)
        if (!this.ThreeWGLrenderer) {
            this.onInitializationFailure({ description: `Failed to initialize WebGl context.`, webglfailure: true})
        }
        this.contextInfo = WallspiceWebgl.queryContextInfo(this.ThreeWGLrenderer)
        this.hasRendered = false

        this.glMemoryRecorder = null
        if (this.ThreeWGLrenderer) {
            this.glMemoryRecorder = new GlMemoryRecorder(this.ThreeWGLrenderer.getContext())
            this.glMemoryRecorder.start()
        }

        this.wallTexture = null

        this.settings = settings

        /* webgl has support for meassuring elapsed time for execution of wegl commands via an extension.
         Note that all webgl commands are invoked asynchroneously. This extension injects a marker into 
         the command stream and valid query results is readback a few frames later. 
         For further details, please take a look at:
         https://www.khronos.org/registry/webgl/extensions/EXT_disjoint_timer_query_webgl2/ 
         */
        this.gpuFrametime = {
            enable: false,
            enableQuery: true,
            enableStats: false,
            lastQueryResultMS: 0,
            mintime: Infinity,
            maxtime: -Infinity,
            cumulativeAvg: 0,
            numSamples: 0,
            ext: this.ThreeWGLrenderer && this.ThreeWGLrenderer.getContext().getExtension(WGL_GPU_FRAME_TIME_EXT_NAME)
        }

        this.maxTextureSize = maxTextureSize || DEFAULT_MOBILE_TEXTURE_SIZE
        this.textureHelper = new TextureHelper(this)

        this.debugSettings = debugSettings
        this.debugVizObjects = []
        this.debugLightHelpers = []
        this.updateDebugSettings(this.debugSettings)

        this.shadowfloor = null
        this.shadowfloorBlack = null
        this.highlightMesh = null
        this.occlusionMesh = null
        this.segmentsMesh = null
        this.emptyOcclusionMesh = null
        this.occlusionImage = null
        this.foregroundMesh = null
        this.emptyRGB = null
        this.unemptyRGB = null
        this.segsData = null
        this.segsDataFeather = null
        this.segsTex = null
        this.paintData = null
        this.paintTex = null
        this.highlightSeg = 1024
        this.paintSet = saveData.erasedSegments ? new Set(saveData.erasedSegments) : new Set()

        this.initialWallPaint = saveData.wallPaint || null
        this.compositeBg = null

        this.fullyInitialized = false

        this.sceneMid = this.generateLitScene(0) // eventually gone
        this.mid = false
        this.sceneFront = this.generateLitScene(1)
        this.sceneOverlay = this.generateImageOverlayScene()

        this.aaType = null
        this.composer = null
        this.renderPass = null
        this.outlinePass = null
        this.antiAliasPass = null

        this.resetLights()
        if (!this.debugSettings.useMultiShadow) {
            this.allLightOneShadow()
        }

        this.shouldRender = true
        this.renderIsDirty = false
        // if previous frame was dirty, used for gathering frame metrics
        this.renderWasDirty = false
        // if any items were loading during last render
        this.itemsWereLoading = false
        // if magic window was loading during last render
        this.magicWindowWasLoading = false

        const frameBins = [
            { title: 'frame 0-15 ms', value: 15.0 },
            { title: 'frame 15-40 ms', value: 40.0 },
            { title: 'frame 40-100 ms', value: 100.0 },
            { title: 'frame 100-200 ms', value: 200.0 },
            { title: 'frame 200-500 ms', value: 500 },
            { title: 'frame 500-1000 ms', value: 1000.0 },
            { title: 'frame 1000-2000 ms', value: 2000.0 },
            { title: 'frame 2000-inf ms', value: null },
        ]
        this.frameMetric = new TimeMetric(frameBins)
        this.frameMetricLoading = new TimeMetric(frameBins.map(obj => ({
            title: `loading ${obj.title}`,
            value: obj.value,
        })))
        this.activeFrameMetric = null

        this.stackingPlanes = new Map()
        this.renderItems = new Map()
        this.raycaster = new THREE.Raycaster(new THREE.Vector3(), new THREE.Vector3(), 0.1, 10000.0)
        this.hasStaticStackableSurfaces = false

        // used during loading of a saved composition to re-link items to corresponding surfaces
        this.delayedStackedLinks = new Map()

        this.oneTimeRenderCallbacks = []
        this.scheduledRenderCallbacks = []

        this.animationFrameId = null
        this.animate = this.animate.bind(this)
        this.animate()

        this.footprintMaterial = null
        this.floorWorldPlane = new THREE.Plane(new THREE.Vector3(0.0, 1, 0.0).normalize(), 0.0)
        this.loadWallData()
    }

    static getInstance() {
        return rendererInstance
    }

    static newInstance(params) {
        rendererInstance = new Renderer(params)
    }

    isRendererValid() {
        return this.ThreeWGLrenderer !== null
    }

    setGPUframetime(onoff) {
        if (onoff === this.gpuFrametime.enable) {
            return
        }

        if (onoff && !this.gpuFrametime.queryObj) {
            if (this.gpuFrametime.ext && typeof this.ThreeWGLrenderer.getContext().createQuery !== 'undefined') {
                this.gpuFrametime.queryObj = this.ThreeWGLrenderer.getContext().createQuery()
                this.gpuFrametime.enable = true
            }
        } else {
            if (this.gpuFrametime.queryObj) {
                const gl = this.ThreeWGLrenderer.getContext()
                gl.deleteQuery(this.gpuFrametime.queryObj)
            }
            this.gpuFrametime.queryObj = null
            this.gpuFrametime.enable = false
        }

        this.enableGPUframeStats(onoff)
    }

    enableGPUframeStats(onOff) {
        this.gpuFrametime.enableStats = onOff
        if (this.gpuFrametime.enableStats) {
            const debugInfoDiv = document.createElement('div')
            debugInfoDiv.setAttribute('id', 'debugInfo')
            this.gpuFrametime.stats = new Stats()
            this.gpuFrametime.queryPanel = this.gpuFrametime.stats.addPanel(new Stats.Panel('webgl-ms', '#ff8', '#221'))
            this.gpuFrametime.stats.showPanel(3)
            this.gpuFrametime.stats.domElement.setAttribute('id', 'gpuftstats')
            debugInfoDiv.appendChild(this.gpuFrametime.stats.domElement)

            // create a pre as a container for textnode as it handles \n escape for new lines.
            const newpre = document.createElement('pre')
            newpre.setAttribute('id', 'gpuftpre')
            const newcontent = document.createTextNode('GPU frame times: ')
            newpre.appendChild(newcontent)
            debugInfoDiv.appendChild(newpre)

            this.canvas.parentNode.appendChild(debugInfoDiv)
            this.gpuFrametime.statsTextDom = newcontent

            this.gpuFrametime.debugInfoDiv = debugInfoDiv
        }
        else if (this.gpuFrametime.debugInfoDiv) {
            this.canvas.parentNode.removeChild(this.gpuFrametime.debugInfoDiv)
            this.gpuFrametime.debugInfoDiv = null
        }
    }

    onMouseMove(e) {
        if (this.settings.magicEraserMode)
            this.updateHighlightFromPosition(e, true)
    }

    onClick(e) {
        if (this.settings.magicEraserMode) {
            this.infillSegmentFromPosition(e)
            this.updateHighlightFromPosition(e, false)
        }
    }

    eraseRegionPlaneIDs(regionID) {
        if (!this.eraseRegionSurfaceCache)
            this.eraseRegionSurfaceCache = new Map()

        const cachedResult = this.eraseRegionSurfaceCache.get(regionID)

        if (cachedResult)
            return cachedResult

        const ids = new Set()
        this.eraseRegionSurfaceCache.set(regionID, ids)

        const gridPoints = this.eraseRegionGridPoints.get(regionID)

        if (!gridPoints)
            return ids

        for(let i = 0; i < gridPoints.length; i++) {
            const p = gridPoints[i];

            const pids = this.getStaticSurfacePlaneIDsAtPoint(p[0], p[1])

            pids.forEach(pid => {
                ids.add(pid)
            })
        }

        return ids
    }

    getStaticSurfacePlaneIDsAtPoint(relX, relY) {
        this.setupRaycaster(relX, relY)

        const targets = []

        if (this.sceneMid)
            targets.push(this.sceneMid.children)

        if (this.sceneFront)
            targets.push(this.sceneFront.children)

        const staticPlaneHits = targets.flatMap(target => {
            const hits = this.raycaster.intersectObjects(target, true)

            return hits.filter(
                hit => (
                    hit
                    && hit.object
                    && hit.object.userData
                    && hit.object.userData.isStaticSurface
                    && hit.object.stackablePlaneId
                )
            )
        })

        return staticPlaneHits.map(hit => hit.object.stackablePlaneId)
    }

    updateHiddenSurfaces() {
        if (!this.staticStackablesLoaded || !this.eraseRegionGridPoints || !this.segsData)
            return

        //
        // This.eraseRegionSurfaceCache is used inside of
        // eraseRegionPlaneIDs(). In scenes with many erase regions
        // eraseRegionPlaneIDs() may need to perform hundreds of ray
        // casts. It does does these ray casts just once and caches
        // the results. A few hundreds ray casts only takes 100-200ms,
        // but it can make the "Hide All" button appear to have
        // a slight delay the first time it is pressed. The code in
        // this block warms the cache to prevent this delay. This
        // method triggers when first loading the room, so this cache
        // warming is guaranteed to happen.
        //

        if (!this.eraseRegionSurfaceCache) {
            this.segsData.segments.forEach(segID => {
                // Calling this method warms its internal
                // cache. We call it but ignore the results.
                this.eraseRegionPlaneIDs(segID)
            })
        }

        const surfs = new Set()

        this.paintSet.forEach(segID => {
            this.eraseRegionPlaneIDs(segID).forEach(id => {
                surfs.add(id)
            })
        })

        this.doUpdateHiddenSurfaces([...surfs])
    }

    syncPaintSet() {
        this.updateSaveData()
        this.updateHiddenSurfaces()
    }

    updateSaveData() {
        if (!this.segsData)
            return

        const saveData = {
            erasedSegments: [...this.paintSet],
        }
        const trackingInfo = {
            EraseableItems: this.segsData.numSegments,
        }

        this.onUpdateSaveData(saveData, trackingInfo)
    }

    incrementLoadedItems(size) {
        this.loadedItems += 1
        this.loadedTotalSize += size
    }

    decrementLoadedItems(size) {
        this.loadedItems -= 1
        this.loadedTotalSize -= size
    }

    incrementLoadingItems() {
        this.loadingItems += 1
    }

    decrementLoadingItems() {
        this.loadingItems -= 1
    }

    getWallDimensions() {
        return [this.wallWidth, this.wallHeight]
    }

    getRenderDimensions() {
        if (this.depthData) {
            return [this.depthData.width, this.depthData.height]
        }
        if (this.maskData) {
            return [this.maskData.width, this.maskData.height]
        }
        return null
    }

    setMaskData(maskData) {
        if (!maskData || !maskData.data) {
            return
        }

        if (this.maskG) {
            this.maskData = maskData
            // mask and shift green channel to blue (for now)
            for (let i = 0; i < this.maskData.width * this.maskData.height; i += 1) {
                const pix = i * 4
                this.maskData.data[pix + 2] = this.maskData.data[pix + 1]
            }
        } else {
            this.maskData = maskData
        }
        this.updateFloorLines()
    }

    async loadWallData() {
        if (!this.sceneData || !this.manifest) {
            return
        }

        const results = await Promise.all([
            loadManifestTexture(this.sceneData.manifest, "pano_0", this.defaultDownloadOptions),
            loadManifestImagePixels(this.sceneData.manifest, this.maskDataURL, this.textureHelper)
        ])

        if (!this.activeRenderer) return

        const wallTexture = results[0] ? results[0].texture : null
        const maskData = results[1] ? results[1].pixels : null

        this.additionalDataLoadSize += results.reduce(
            (accum, curr) => accum + (curr ? curr.size : 0),
            0.0
        )

        this.wallTexture = wallTexture
        this.setMaskData(maskData)
        this.depthData = null

        if (this.wallTexture) {
            this.wallTexture.flipY = false
            this.wallTexture.encoding = THREE.sRGBEncoding
            this.wallTexture.needsUpdate = true
        }

        this.lateInitialization()

        this.onWallDataReady(this.wallWidth, this.wallHeight)
    }

    lateInitialization() {
        this.wallWidth = (this.wallTexture && this.wallTexture.image) ? this.wallTexture.image.width : 1
        this.wallHeight = (this.wallTexture && this.wallTexture.image) ? this.wallTexture.image.height : 1

        this.camera = WallspiceWebgl.createCamera(this.intrinsics, this.wallWidth, this.wallHeight)

        Sentry.addBreadcrumb({
            category: SENTRY_BREADCRUMB_CATEGORY,
            message: 'Initial canvas size',
            data: {
                width: this.canvas.width,
                height: this.canvas.height,
                wallWidth: this.wallWidth,
                wallHeight: this.wallHeight,
            }
        })

        // Sometimes (rarely) when the tab is inactive, react throttles the components update function calls 
        // causing the renderer to initialize before the canvas dimension are set by the PreviewContainer
        if (!this.canvas.width) {
            this.canvas.width = this.settings.canvasWidth || getMaxWindowSize()
        }

        this.canvas.height = this.wallWidth > 0 ? this.canvas.width * (this.wallHeight / this.wallWidth) : 0

        Sentry.addBreadcrumb({
            category: SENTRY_BREADCRUMB_CATEGORY,
            message: 'Final canvas size',
            data: {
                width: this.canvas.width,
                height: this.canvas.height,
            }
        })

        if (this.ThreeWGLrenderer) {
            this.ThreeWGLrenderer.setSize(this.canvas.width, this.canvas.height)
        }

        this.sceneBack = this.generateBackgroundScene()

        if (!isMobile()) {
            this.generateOcclusionScene().then((scene) => {
                this.sceneOcclusion = scene
            }).catch((error) => {
                if (error) {
                    // eslint-disable-next-line no-console
                    console.error(error)
                    Sentry.captureException(new Error('Error during occlusion scene generation.', { cause: error }))
                }
            }).finally(() => {
                this.fullyInitialized = true
            })
        } else {
            // assuming here that the code below is synchronous
            this.fullyInitialized = true
        }

        this.sceneOverlay = this.generateImageOverlayScene()
        this.createForegroundMesh(this.sceneMid)

        if (!this.composer) {
            this.initPostprocessing()
        } else {
            this.composer.setSize(this.canvas.width, this.canvas.height)
            this.outlinePass.camera = this.camera
        }

        // re-apply 2d positions and update render items properties
        this.renderItems.forEach(item => {
            item.refresh2DPosition()
            if (item.update) {
                item.update()
            }
        })

        this.setRenderIsDirty()
    }

    initFloorPlane() {
        if (!this.manifest || !this.planeOrientation || !this.occlusionImage) {
            return
        }
        const floorPlaneIdx = this.planeOrientation.planeData.findIndex(plane => plane.class === 'floor')
        if (floorPlaneIdx < 0) {
            return
        }
        const floorPlane = this.planeOrientation.planeData[floorPlaneIdx]

        this.occlusionTexture = new THREE.DataTexture(
            this.occlusionImage.data,
            this.occlusionImage.width,
            this.occlusionImage.height,
        )
        this.occlusionTexture.flipY = true
        this.occlusionTexture.needsUpdate = true

        this.floorWorldPlane = new THREE.Plane(new THREE.Vector3(floorPlane.rot[2][0], floorPlane.rot[2][1], floorPlane.rot[2][2]), floorPlane.d)

        const resolution = new THREE.Vector2(this.canvas.width, this.canvas.height)
        this.footprintMaterial = new FootprintMaterial(this.floorWorldPlane, this.occlusionTexture, resolution)
    }

    initPostprocessing() {
        if (!this.ThreeWGLrenderer || isMobile()) {
            return
        }

        this.composer = new EffectComposer(this.ThreeWGLrenderer)
        this.composer.renderTarget1.texture.encoding = THREE.sRGBEncoding
        this.composer.renderTarget1.stencilBuffer = true
        this.composer.renderTarget2.texture.encoding = THREE.sRGBEncoding
        this.composer.renderTarget2.stencilBuffer = true

        this.renderPass = new CustomRenderPass(this.drawTheThings.bind(this))
        this.composer.addPass(this.renderPass)

        this.outlinePass = new CustomOutlinePass(new THREE.Vector2(this.canvas.width, this.canvas.height),
            this.sceneFront, this.camera, [], this.sceneOcclusion)
        this.outlinePass.enabled = this.showOutlines
        this.outlinePass.visibleEdgeColor.set(0xffdb00)
        this.outlinePass.selectedEdgeColor.set(0xffdb00)
        this.outlinePass.hiddenEdgeColor.set(0xffdb00)
        this.outlinePass.edgeThickness = EDGE_THICKNESS_THIN
        this.outlinePass.edgeStrength = EDGE_STRENGTH_THIN
        this.composer.addPass(this.outlinePass)

        this.setAntiAliasing(AA_TAA)
    }

    updateFloorLines() {
        if (!this.maskData)
            return

        this.floorLines = WallSnap.makeFloorLines(
            this.planeOrientation.planeData,
            this.maskData,
            this.intrinsics
        )
    }

    setupRaycaster(relX, relY) {
        const start = new THREE.Vector3(0.0, 0.0, 0.0)
        const fx = this.intrinsics[0]
        const fy = this.intrinsics[1]
        const ppx = this.intrinsics[2]
        const ppy = this.intrinsics[3]
        const w = this.wallWidth
        const h = this.wallHeight

        const direction = new THREE.Vector3((w * relX - ppx) / fx, -(h * relY - ppy) / fy, -1.0)
        direction.normalize()
        this.raycaster.set(start, direction)
        this.raycaster.layers.enableAll()
    }

    getTargetAtPoint(relX, relY) {
        this.setupRaycaster(relX, relY)

        let target = this.sceneMid
            ? this.sceneMid.children
            : null

        let objHit = null
        let range = 10000
        for (let tgts = 0; tgts < 2; tgts += 1) {
            const intersects = target ? this.raycaster.intersectObjects(target, true) : []
            for (let i = 0; i < intersects.length; i += 1) {
                if (intersects[i].face && Renderer.isObjectVisible(intersects[i].object)) {
                    if (intersects[i].distance < range) {
                        if (
                            intersects[i].object.name
                            && intersects[i].object.name !== 'lightmap blend'
                            && intersects[i].object.name !== 'shadow floor receiver'
                            && intersects[i].object.name !== 'shadow floor black'
                            && intersects[i].object.name !== 'occlusion image'
                            && intersects[i].object.name !== 'foreground image'
                            && intersects[i].object.name !== 'depth occlusion modifier'
                        ) {
                            range = intersects[i].distance
                            objHit = intersects[i].object
                        }
                    }
                }
            }
            target = this.sceneFront
                ? this.sceneFront.children
                : null
        }
        return { range, objHit }
    }

    static isObjectVisible(obj) {
        if (!obj) {
            return false
        }
        if (obj.visible && obj.parent) {
            return Renderer.isObjectVisible(obj.parent)
        }
        return obj.visible
    }

    getObjectIdAtPoint(relX, relY) {
        const intersect = this.getTargetAtPoint(relX, relY)
        let id = null
        if (intersect.objHit) {
            let idName = intersect.objHit.name
            while (idName != null && idName.search('renderObject-') !== 0) {
                idName = intersect.objHit.name
                if (!intersect.objHit.parent) {
                    break
                }
                intersect.objHit = intersect.objHit.parent
            }
            idName = idName.replace('renderObject-', '')
            id = parseInt(idName, 10)
        }
        return id;
    }

    getStackableSurfaceAtPoint(relX, relY) {
        const intersect = this.getTargetAtPoint(relX, relY)
        if (intersect.objHit && intersect.objHit.stackablePlaneId !== undefined) {
            return intersect.objHit.stackablePlaneId
        }
        return null
    }

    addToScene(renderItem) {
        if (this.mid && renderItem.getLayer() === 0) {
            if (this.sceneMid) {
                this.sceneMid.add(renderItem.getObject())
            }
        } else if (this.sceneFront) {
            this.sceneFront.add(renderItem.getObject())
        }
        this.setRenderIsDirty()
        this.addToRenderItems(renderItem)
        this.renderItems.forEach((item) => {
            if (item.isCombinableChild && item.isCombinableChild()) {
                item.refreshCombinableParent()
            }
        })
    }

    addToRenderItems(renderItem) {
        this.renderItems.set(renderItem.id, renderItem)
    }

    addDelayedStackedLink(planeId, furnitureItem) {
        if (!this.delayedStackedLinks.has(planeId)) {
            this.delayedStackedLinks.set(planeId, [])
        }
        const existing = this.delayedStackedLinks.get(planeId)
        existing.push(furnitureItem)
    }

    addStackingSurface(stackableSurfaceItem) {
        if (!stackableSurfaceItem) {
            return
        }
        if (!stackableSurfaceItem.parent) {
            this.addToScene(stackableSurfaceItem)
            this.hasStaticStackableSurfaces = true

            const staticStackablesInScene = this.sceneData.stackables.length

            if( ! this.staticStackableCount )
                this.staticStackableCount = 0

            this.staticStackableCount += 1

            if( staticStackablesInScene === this.staticStackableCount ) {
                this.staticStackablesLoaded = true
                this.syncPaintSet()
            }
        }
        const { renderObject } = stackableSurfaceItem
        this.stackingPlanes.set(renderObject.stackablePlaneId, stackableSurfaceItem)

        const waitingItems = this.delayedStackedLinks.get(renderObject.stackablePlaneId)
        if (Array.isArray(waitingItems)) {
            waitingItems
                .forEach(furnitureItem => furnitureItem.delayedSetPlane(renderObject.stackablePlaneId))
            this.delayedStackedLinks.delete(renderObject.stackablePlaneId)
        }
    }

    getStackingSurface(surfaceId) {
        return this.stackingPlanes.get(surfaceId)
    }

    getRenderItem(itemId) {
        return this.renderItems.get(itemId)
    }

    removeFromScene(renderItem) {
        if (this.mid && renderItem.getLayer() === 0) {
            if (this.sceneMid) {
                this.sceneMid.remove(this.sceneMid.getObjectByName(renderItem.getObject().name))
            }
        } else if (this.sceneFront) {
            this.sceneFront.remove(this.sceneFront.getObjectByName(renderItem.getObject().name))
        }
        // may be selcted at the moment of deletion, in which case will be retained by outline pass
        this.removeObjectFromOutline(renderItem.getObject())
        this.renderItems.delete(renderItem.id)
        this.setRenderIsDirty()
    }

    generateBackgroundScene() {
        if (!this.wallTexture) {
            return null
        }
        const scene = WallspiceWebgl.createScene()
        const isCompositeBg = (!!this.manifest.residual_0 || !!this.manifest.residual_0_webp)
            && (!!this.manifest.shading_0 || !!this.manifest.shading_0_webp)
        if (isCompositeBg) {
            this.compositeBg = WallspiceWebgl.createCompositeBackground(
                this,
                this.intrinsics,
                this.wallWidth, this.wallHeight,
                this.manifest,
                this.wallTexture,
            )
            this.unemptyRGB = this.compositeBg.fullscreenQuad
            if (this.initialWallPaint) {
                Object.keys(this.initialWallPaint).forEach((key) => {
                    this.compositeBg.setPlaneColor(parseInt(key, 10), this.initialWallPaint[key], true)
                })
            }
        } else {
            this.unemptyRGB = WallspiceWebgl.createBackground(
                this.intrinsics,
                this.wallWidth, this.wallHeight,
                this.wallTexture
            )
            scene.add(this.unemptyRGB)
            this.unemptyRGB.visible = true
        }
        scene.add(this.unemptyRGB)
        this.unemptyRGB.visible = true

        loadManifestImagePixels(this.manifest, "segmentation_0", this.textureHelper).then(sdData => {
            if (!sdData || !sdData.pixels)
                return

            const sd = sdData.pixels
            const sdSize = sdData.size

            this.additionalDataLoadSize += sdSize

            let sMesh = null
            this.segsData = WallspiceWebgl.isolateChannelDataFromImage(sd, 2)

            this.eraseRegionGridPoints = computeEraseRegionGridPoints(this.segsData)

            this.syncPaintSet()

            this.segsDataFeather = this.segsData
            this.segsDataFeather = WallspiceWebgl.isolateChannelDataFromImage(sd, 3)
            if (!(this.segsData.numSegments > 0 && this.segsData.numSegments === this.segsDataFeather.numSegments)) {
                this.segsDataFeather = this.segsData
            }
            this.segsTex = WallspiceWebgl.createLuminanceTextureFromData(this.segsData)
            sMesh = WallspiceWebgl.createSegmentsMesh(
                this.intrinsics,
                this.wallWidth, this.wallHeight, // needed for intrinsics
                this.segsTex,
                1.0,
                false,
            )
            if (sMesh) {
                this.segmentsMesh = sMesh
                scene.add(sMesh)
            }

            this.emptyRGB = WallspiceWebgl.createBackgroundFromURL(
                this.intrinsics,
                this.wallWidth, this.wallHeight,
                this.manifest.pano_inpaint_0,
                this.getWallDimensions(),
            )

            scene.add(this.emptyRGB)
            this.emptyRGB.visible = false
        })

        if (this.maskData && this.planeOrientation) {
            this.highlightMesh = WallspiceWebgl.createPlaceableOverlay(
                this.intrinsics,
                this.wallWidth, this.wallHeight, // needed for intrinsics
                this.maskData,
                this.planeOrientation.planeData,
                0.2,
            )
        }
        if (this.highlightMesh) {
            scene.add(this.highlightMesh)
            this.highlightMesh.visible = false
        }

        if (this.maskData) {
            const normalSegData = WallspiceWebgl.isolateChannelDataFromImage(this.maskData, 2)
            const normalSegTex = WallspiceWebgl.createLuminanceTextureFromData(normalSegData)
            this.debugSegmentsMesh = WallspiceWebgl.createDebugSegmentsMesh(
                this.intrinsics,
                this.wallWidth, this.wallHeight, // needed for intrinsics
                normalSegTex,
                1.0,
                false,
            )
        }
        if (this.debugSegmentsMesh) {
            scene.add(this.debugSegmentsMesh)
        }

        return scene
    }

    async generateOcclusionScene() {
        if (!this.ThreeWGLrenderer) {
            return null
        }

        const scene = WallspiceWebgl.createScene()

        const depthOcclusionData = await this.loadDepthOcclusion(scene)

        if (!this.activeRenderer) return null

        this.updateDebugSettings()
        this.onReady(this.contextInfo)

        if (depthOcclusionData) {
            await this.loadDepthOcclusionInpaint(
                scene,
                depthOcclusionData.tex,
                depthOcclusionData.mesh
            )
        }

        return scene
    }

    async loadDepthOcclusion(scene) {
        const data = await loadManifestTexture(this.manifest, "depth_occlusion_0", this.defaultDownloadOptions)

        if (!data || !data.texture || !this.activeRenderer) {
            return null
        }

        const tex = data.texture
        const size = data.size

        this.additionalDataLoadSize += size

        this.occlusionImage = this.textureHelper.readPixels(tex)
        this.initFloorPlane() // needs this.occlusionImage
        if (!this.manifest.depth_occlusion_meta_0) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const depth_occlusion_meta_0 = this.mockDepthOcclusionMeta();
            [...this.directionalLights, ...this.pointLights].forEach(light => WallspiceWebgl
                .fitShadowMap(light, this.camera, depth_occlusion_meta_0)) // needs this.occlusionImage
        }

        const hasFragmentDepth = this.contextInfo && this.contextInfo.hasFragmentDepth

        let mesh = null
        if (hasFragmentDepth) {
            mesh = WallspiceWebgl.createDepthOcclusionFromImage(
                this.intrinsics,
                this.wallWidth, this.wallHeight, // needed for intrinsics
                tex,
                2.0,
                !this.settings.magicEraserMode,
            )
            if (mesh !== null) {
                const ext = this.manifest.extra
                WallspiceWebgl.setOcclusionNearFar(
                    mesh,
                    (ext && ext.nearValue) || 0.2,
                    (ext && ext.farValue) || 30,
                )
            }
        } else if (this.occlusionImage) {
            mesh = WallspiceWebgl.createMeshOcclusionFromImage(
                this.intrinsics,
                this.wallWidth, this.wallHeight, // needed for intrinsics
                this.occlusionImage,
                !this.settings.magicEraserMode,
            )
        }

        if (mesh !== null) {
            this.occlusionMesh = mesh
            scene.add(mesh)
        }

        return { tex, mesh }
    }

    async loadDepthOcclusionInpaint(scene, depthOcclusionTex, depthOcclusionMesh) {
        const hasFragmentDepth = this.contextInfo && this.contextInfo.hasFragmentDepth

        if (!hasFragmentDepth)
            return

        const data = await loadManifestTexture(this.manifest, "depth_occlusion_inpaint_0", this.defaultDownloadOptions)

        if (!data || !data.texture || !this.activeRenderer)
            return

        const tex = data.texture
        const size = data.size

        this.additionalDataLoadSize += size

        const zMesh = WallspiceWebgl.createDepthMuxOcclusionFromImages(
            this.intrinsics,
            this.wallWidth, this.wallHeight, // needed for intrinsics
            depthOcclusionTex,
            tex,
            2.0,
            !this.settings.magicEraserMode,
        )

        if (zMesh !== null) {
            const ext = this.manifest.extra
            WallspiceWebgl.setOcclusionNearFar(
                depthOcclusionMesh,
                (ext && ext.nearValue) || 0.2,
                (ext && ext.farValue) || 30,
            )
        }

        if (depthOcclusionMesh !== null) {
            this.emptyOcclusionMesh = zMesh
            // this.emptyOcclusionMesh.visible = false
            scene.add(zMesh)
        }

        this.initInfillIfNeeded()
    }

    mockDepthOcclusionMeta() {
        let min = 10000
        let max = -10000
        if (!this.occlusionImage) {
            // If no depth data available, mock the scene limits using the camera's near/far properties
            return { min: this.camera.near, max: this.camera.far }
        }
        for (let i = 0; i < this.occlusionImage.data.length; i += 4) {
            // Equation used from the manifest docs to get depth in meters
            const z = 0.0000390625 * (
                this.occlusionImage.data[i] * 65536.0
                + this.occlusionImage.data[i + 1] * 256.0
                + this.occlusionImage.data[i + 2]
            )
            min = Math.min(min, z)
            max = Math.max(max, z)
        }
        return { min, max }
    }

    generateImageOverlayScene() {
        const scene = WallspiceWebgl.createScene()

        this.iuglyMesh = WallspiceWebgl.createImgPaste(
            this.intrinsics,
            this.wallWidth, this.wallHeight, // needed for intrinsics
        )
        if (this.iuglyMesh !== null) {
            scene.add(this.iuglyMesh)
        }

        this.iMesh = WallspiceWebgl.createImgPaste(
            this.intrinsics,
            this.wallWidth, this.wallHeight, // needed for intrinsics
        )
        if (this.iMesh !== null) {
            scene.add(this.iMesh)
        }

        return scene
    }

    generateLitScene(layer) {
        if (!this.ThreeWGLrenderer) {
            return null
        }

        const scene = WallspiceWebgl.createScene()
        let directBright = 0.6
        let ambientBright = 0.45
        let lights = null

        if (layer === 0) {
            if (this.sceneData.hasWall) {
                directBright = 0.15
                ambientBright = 0.55
                this.createForegroundMesh(scene)
            }

            // Equirectangular unproject
            const pmremGenerator = new THREE.PMREMGenerator(this.ThreeWGLrenderer);
            pmremGenerator.compileEquirectangularShader()
            THREE.DefaultLoadingManager.onLoad = () => {
                pmremGenerator.dispose()
                THREE.DefaultLoadingManager.onLoad = undefined
            };

            // default envmap
            let probepath = `${CDN_BUCKET}/testAssets/cubemap/`
            let equirectangular = 'halfFurnished_HDRnoflip.hdr'
            // if(this.manifest.equirectangular_yflip) {
            //     probepath = ''
            //     equirectangular = this.manifest.equirectangular_yflip
            // } else
            if (this.manifest.equirectangular) {
                probepath = ''
                equirectangular = this.manifest.equirectangular
            }
            // HDR load
            this.cubeTex = new Promise((resolve) => {
                new RGBELoader()
                    .setPath(probepath)
                    .setDataType(THREE.UnsignedByteType)
                    .load(equirectangular, (texture) => {
                        const cubetex = pmremGenerator.fromEquirectangular(texture).texture
                        pmremGenerator.dispose()
                        texture.dispose()
                        resolve(cubetex)
                    });
            })
        } else {
            // eslint-disable-next-line no-lonely-if
            if (this.manifest && this.planeOrientation) {
                const planes = this.planeOrientation
                if (planes.planeData && planes.planeData.length) {
                    let i
                    for (i = 0; i < planes.planeData.length; i += 1) {
                        if (planes.planeData[i].class === 'floor') {
                            break;
                        }
                    }

                    if (i < planes.planeData.length) {
                        const { software } = this.contextInfo
                        const shadowobj = WallspiceWebgl.createAppMesh(planes.planeData[i], 0, software)
                        if (shadowobj) {
                            shadowobj.visible = this.settings.showShadows
                            scene.add(shadowobj)
                            this.shadowfloor = shadowobj;
                        }

                        const blackobj = WallspiceWebgl.createAppMesh(planes.planeData[i], 4, software)
                        if (blackobj) {
                            blackobj.visible = false
                            scene.add(blackobj)
                            this.shadowfloorBlack = blackobj;
                        }

                        let debugobj = WallspiceWebgl.createAppMesh(planes.planeData[i], 1, software)
                        if (debugobj) {
                            this.debugVizObjects.push(debugobj)
                            scene.add(debugobj)
                        }
                        debugobj = WallspiceWebgl.createAppMesh(planes.planeData[i], 2, software)
                        if (debugobj) {
                            this.debugVizObjects.push(debugobj)
                            scene.add(debugobj)
                        }
                        debugobj = WallspiceWebgl.createAppMesh(planes.planeData[i], 3, software)
                        if (debugobj) {
                            this.debugVizObjects.push(debugobj)
                            scene.add(debugobj)
                        }
                        this.updateDebugSettings()
                    }
                }
            }
        }

        if (this.manifest.lighting) { // got manifest?
            lights = this.manifest.lighting.lights
            // make sure we disregard old manifest lights
            if (!lights.ambient.intensity) {
                lights = null
            } else
                if (lights.directional[0] && !lights.directional[0].intensity) {
                    lights = null
                } else
                    if (lights.point[0] && !lights.point[0].intensity) {
                        lights = null
                    }
        }

        if (lights === null) {
            scene.add(WallspiceWebgl.createAmbientLight(ambientBright))
            if (layer === 1) {
                scene.add(WallspiceWebgl.createDirectionalLight(new THREE.Vector3(-1, 1, 1.5), directBright, true, this.maxTextureSize))
            } else {
                scene.add(WallspiceWebgl.createDirectionalLight(new THREE.Vector3(-1, 1, 3.5), directBright, false, this.maxTextureSize))
            }
        } else {
            let shadow = true
            if (layer === 0) {
                shadow = false
            }
            let totalLights = 0
            this.ambientLight = WallspiceWebgl.createReducedAmbientColoredLight(lights.ambient)
            scene.add(this.ambientLight)
            lights.directional.forEach((light, index) => {
                if (totalLights < 8) {
                    const objlight = WallspiceWebgl.createDirectionalColoredLight(light, shadow, this.maxTextureSize)
                    scene.add(objlight)
                    scene.add(objlight.target)
                    if (layer === 1) {
                        const olp = objlight.position
                        // eslint-disable-next-line no-param-reassign
                        objlight.ext_centerPosition = new THREE.Vector3(olp.x, olp.y, olp.z)
                        objlight.ext_size = Math.sqrt(light.size) * 0.01
                        if (light.volume) {
                            objlight.ext_volume = [
                                new THREE.Vector3(light.volume[0][0], light.volume[0][1], light.volume[0][2]),
                                new THREE.Vector3(light.volume[1][0], light.volume[1][1], light.volume[1][2]),
                                new THREE.Vector3(light.volume[2][0], light.volume[2][1], light.volume[2][2])]
                        }

                        // Fit the directional light shadow mapping properties to room dimensions
                        WallspiceWebgl.fitShadowMap(objlight, this.camera, this.manifest.depth_occlusion_meta_0)

                        // Move directional light helpers into the camera's frustum
                        const cameraFront = this.camera.getWorldDirection(new THREE.Vector3())
                        const targetDeltaPos = objlight.target.position.clone().sub(objlight.position)
                        const cameraRight = cameraFront.clone().cross(this.camera.up)
                        const lightDirection = targetDeltaPos.clone().normalize()
                        const angleToHorizon = lightDirection.projectOnPlane(cameraFront).angleTo(cameraRight)
                        const sideTranslation = cameraRight.multiplyScalar(((angleToHorizon / Math.PI) - 0.5))
                        const tempLight = objlight.clone(true)
                        tempLight.position.copy(this.camera.position).add(cameraFront.multiplyScalar(2)).add(sideTranslation)
                        tempLight.target.position.copy(tempLight.position.clone().add(targetDeltaPos))
                        tempLight.updateMatrixWorld(true)
                        tempLight.target.updateMatrixWorld(true)
                        const debugobj = WallspiceWebgl.createLightHelper(tempLight, 0.1, 0x000000)
                        const debugLabel = WallspiceWebgl.createTextHelper(`${index}`, 8, debugobj.position)
                        if (debugobj) {
                            debugobj.add(debugLabel)
                            this.debugLightHelpers.push(debugobj)
                            scene.add(debugobj)
                            objlight.ext_debugviz = debugobj
                        }
                        this.directionalLights.push(objlight)
                    }
                    if (shadow && this.shadowfloor) {
                        WallspiceWebgl.setOpacity(this.shadowfloor, light.intensity)
                    }
                }
                // shadow = false
                totalLights += 1
            })
            lights.point.forEach((light, index) => {
                if (totalLights < 8) {
                    const objlight = WallspiceWebgl.createPointColoredLight(light, shadow, this.maxTextureSize)
                    scene.add(objlight)
                    if (layer === 1) {
                        const olp = objlight.position
                        // eslint-disable-next-line no-param-reassign
                        objlight.ext_centerPosition = new THREE.Vector3(olp.x, olp.y, olp.z)
                        objlight.ext_size = Math.sqrt(light.size) * 0.01
                        if (light.volume) {
                            objlight.ext_volume = [
                                new THREE.Vector3(light.volume[0][0], light.volume[0][1], light.volume[0][2]),
                                new THREE.Vector3(light.volume[1][0], light.volume[1][1], light.volume[1][2]),
                                new THREE.Vector3(light.volume[2][0], light.volume[2][1], light.volume[2][2])]
                        }
                        const debugobj = WallspiceWebgl.createLightHelper(objlight, 0.1, 0x000000)
                        const debugLabel = WallspiceWebgl.createTextHelper(`${index}`, 12, debugobj.position)
                        if (debugobj) {
                            debugobj.add(debugLabel)
                            this.debugLightHelpers.push(debugobj)
                            scene.add(debugobj)
                            objlight.ext_debugviz = debugobj
                        }
                        this.pointLights.push(objlight)
                    }
                    if (shadow && this.shadowfloor) {
                        WallspiceWebgl.setOpacity(this.shadowfloor, 0.5 /* light.intensity */)
                    }
                }
                // shadow = false
                totalLights += 1
            })
            this.updateDebugSettings()
        }

        return scene
    }

    createForegroundMesh(scene) {
        if (this.mid) {
            if (!scene) {
                return
            }
            if (this.foregroundMesh) {
                scene.remove(this.foregroundMesh)
                this.foregroundMesh = null
            }

            this.foregroundUrl = (this.manifest && this.manifest.foreground_0)
                || this.sceneData.fgMaskUrl
            if (this.foregroundUrl && this.foregroundUrl !== '') {
                this.foregroundMesh = WallspiceWebgl.createForeground(
                    this.intrinsics,
                    this.wallWidth, this.wallHeight, // needed for intrinsics
                    this.foregroundUrl
                )
            }
            if (this.foregroundMesh) {
                scene.add(this.foregroundMesh)
            }
        }
    }

    getLightDirectionByIndex(planeIndex) {
        // TODO: there is no metaData anymore!
        const lightData = this.metaData && this.metaData.lightData
        if (lightData && lightData[planeIndex].length) {
            return DirectionVectorMap[lightData[planeIndex][0].direction]
        }
        return DirectionVectorMap.SOUTHEAST
    }

    findPlaceableFloorPos() {
        const poss = WallspiceWebgl.findPlaceableFloorPositions(
            this.occlusionImage,
            this.wallWidth, this.wallHeight)

        if (poss) {
            let id = null
            for (let count = 0; count < poss.length; count += 1) {
                id = this.getObjectIdAtPoint(
                    poss[count][0] / this.wallWidth,
                    poss[count][1] / this.wallHeight)
                if (id === null) {
                    return poss[count]
                }
            }

            return poss[0]
        }
        return null
    }

    findPlaceableWallPos() {
        const walls = []
        const planes = this.planeOrientation.planeData
        for (let index = 0; index < planes.length; index += 1) {
            if (planes[index] && planes[index].class === 'wall') {
                walls.push(index + 1) // 0 is reserved for blank so add 1
            }
        }
        const poss = WallspiceWebgl.findPlaceableWallPositions(
            this.maskData,
            this.wallWidth, this.wallHeight,
            walls,
        )

        if (poss) {
            let id = null
            for (let count = 0; count < poss.length; count += 1) {
                id = this.getObjectIdAtPoint(
                    poss[count][0] / this.wallWidth,
                    poss[count][1] / this.wallHeight,
                )
                if (id === null) {
                    return poss[count]
                }
            }
        }
        return null
    }

    generatePose(position, isFurniture, isRug) {
        if (this.maskData) {
            return WallspiceWebgl.generatePoseFromPlanes(
                this.intrinsics,
                this.planeOrientation.planeData,
                this.maskData,
                position,
                isFurniture,
                this.debugSettings.defaultToFloor || !(this.manifest.extra && this.manifest.extra.useFloorMask),
                isRug
            )
        }
        if (this.depthData) {
            return WallspiceWebgl.generatePose(
                this.intrinsics,
                this.getWallDimensions(),
                this.depthData,
                position,
            )
        }
        return null
    }

    generatePoseOnPlane(plane, position) {
        return WallspiceWebgl.generatePoseFromPlane(plane, position, this.intrinsics)
    }

    highlightFloorMode(floorMode) {
        if (this.highlightMesh) {
            if (floorMode) {
                WallspiceWebgl.setPlaceableAdjust(this.highlightMesh, -1.0, 0.125)
            } else {
                WallspiceWebgl.setPlaceableAdjust(this.highlightMesh, 1.0, 0.125)
            }
        }
    }

    highlightOn(isFloorMode) {
        if (this.highlightMesh) {
            this.highlightFloorMode(isFloorMode)

            if (this.debugSettings.highlightPlanes) {
                this.highlightMesh.visible = true
            }
        }
    }

    highlightOff() {
        if (this.highlightMesh) {
            this.highlightMesh.visible = false
        }
    }

    applyEraseMask() {
        const eraseTex = this.paintTex
        this.paintTex = WallspiceWebgl.createLuminanceTextureFromData(this.paintData)

        if (this.emptyRGB) {
            WallspiceWebgl.applyMaskTexToMat(this.emptyRGB.material, this.paintTex)
            this.emptyRGB.visible = this.settings.magicEraserMode || (this.paintSet.size > 0)
        }
        if (this.emptyOcclusionMesh) {
            WallspiceWebgl.applyMaskTexToShader(this.emptyOcclusionMesh.material, this.paintTex)
            this.emptyOcclusionMesh.visible = this.settings.magicEraserMode || (this.paintSet.size > 0)
        }
        if (this.occlusionMesh) {
            this.occlusionMesh.visible = !(this.settings.magicEraserMode || (this.paintSet.size > 0))
        }
        if (this.segmentsMesh) {
            WallspiceWebgl.applyMaskTexToShader(this.segmentsMesh.material, this.paintTex)
            this.segmentsMesh.visible = this.settings.magicEraserMode
        }
        if (this.foregroundMesh) {
            this.foregroundMesh.visible = !(this.settings.magicEraserMode || (this.paintSet.size > 0))
        }
        if (eraseTex) {
            eraseTex.dispose()
        }
        this.setRenderIsDirty()
    }


    updateHighlightFromPosition(pos, highlight) {
        if (this.segsData && this.segmentsMesh) {
            let segment = 512
            if (pos[0] >= 0.0 && pos[0] <= 1.0 && pos[1] >= 0.0 && pos[1] <= 1.0) {
                segment = WallspiceWebgl.getValueAtPos(this.segsData, pos)
                if (!highlight || segment !== this.highlightSeg) {
                    if (segment < 1 || segment > 255) {
                        segment = 512 // no highlight
                    }
                    WallspiceWebgl.setSegmentsHighlightIndex(this.segmentsMesh, highlight ? segment : 512)
                    this.highlightSeg = segment
                    this.setRenderIsDirty()
                }
            }
        }
    }

    initInfillIfNeeded() {
        if (this.segsData && this.segmentsMesh) {
            if (this.paintData == null) {
                this.paintData = WallspiceWebgl.generateMask(this.segsData.w, this.segsData.h, 0)
                this.paintSet.forEach((segment) => WallspiceWebgl.keyMask(this.paintData, this.segsDataFeather, segment, 255))
                this.applyEraseMask()
            }
            this.setRenderIsDirty()
        }
    }

    infillSegmentFromPosition(pos) {
        if (this.segsData && this.segmentsMesh) {
            this.initInfillIfNeeded()
            if (pos[0] >= 0.0 && pos[0] <= 1.0 && pos[1] >= 0.0 && pos[1] <= 1.0) {
                const segment = WallspiceWebgl.getValueAtPos(this.segsData, pos)
                const fillvalue = WallspiceWebgl.getValueAtPos(this.paintData, pos)
                if (segment > 0) {
                    if (fillvalue < 128) {
                        WallspiceWebgl.keyMask(this.paintData, this.segsDataFeather, segment, 255)
                        this.paintSet.add(segment)
                        this.syncPaintSet()
                    }
                    else {
                        WallspiceWebgl.keyMask(this.paintData, this.segsDataFeather, segment, 0)
                        this.paintSet.delete(segment)
                        this.syncPaintSet()
                    }
                }
                this.applyEraseMask()
                this.setRenderIsDirty()
            }
        }
    }

    setStaticFurnitureViz(onoff) {
        // Disable the behavior if the room doesn't have the proper segmentation information
        if (!this.segsData || !this.segmentsMesh) {
            return
        }

        // Initialize the paintData mask in case it was still not initialized
        this.initInfillIfNeeded()

        // Apply the needed mask to the paintData and paintSet
        if (onoff) {
            this.paintSet.clear()
            this.segsData.segments.forEach((segment) => 
                WallspiceWebgl.keyMask(this.paintData, this.segsDataFeather, segment, 0))
        } else {
            this.segsData.segments.forEach((segment) => { 
                this.paintSet.add(segment)
                WallspiceWebgl.keyMask(this.paintData, this.segsDataFeather, segment, 255)
            })

        }

        // Apply the updates to the segmentation mask
        this.syncPaintSet()
        this.applyEraseMask()
    }

    updateSettings(settings) {
        if (settings) {
            this.settings = settings
        }

        if (this.shadowfloor) {
            this.shadowfloor.visible = this.settings.showShadows
        }
        if (this.occlusionMesh) {
            this.occlusionMesh.visible = !this.settings.magicEraserMode && (this.paintSet.size === 0)
        }

        if (this.foregroundMesh) {
            this.foregroundMesh.visible = !this.settings.magicEraserMode && (this.paintSet.size === 0)
        }

        if (this.manifest.pano_inpaint_0) {
            if (this.emptyOcclusionMesh) {
                if (this.paintSet.size > 0) {
                    this.emptyOcclusionMesh.visible = true
                } else {
                    this.emptyOcclusionMesh.visible = this.settings.magicEraserMode
                }
            }
            // if (this.unemptyRGB) {
            //     this.unemptyRGB.visible = this.settings.magicEraserMode
            // }
            // if (this.emptyRGB) {
            //     this.emptyRGB.visible = !this.settings.magicEraserMode
            // }

            if (this.segmentsMesh) {
                this.segmentsMesh.visible = this.settings.magicEraserMode
            }
        }

        if (this.settings.disableOutline) {
            this.enableOutline(false)
        }

        if (this.hasRendered) {
            this.setRenderIsDirty()
        }
    }

    updateDebugSettings(settings) {
        if (settings) {
            this.debugSettings = settings
        }
        this.setLinesViz(this.debugSettings.linesViz)
        this.setLightsViz(this.debugSettings.lightsViz)
        this.setDepthViz(this.debugSettings.depthViz)
        this.setSegmenationViz(this.debugSettings.nsViz)
        this.setNormalsViz(this.debugSettings.normalsViz)
        this.setfrontSideShadows(this.debugSettings.frontSideShadows)
        this.setEnvironmentRotation(this.debugSettings.environmentRotation)
        this.setEnvironmentIntensity(this.debugSettings.environmentIntensity)
        this.setLightsIntensity(
            this.debugSettings.pointLightIntensities,
            this.debugSettings.ambientLightIntensity,
            this.debugSettings.directionalLightIntensities,
        )
        this.setAnalyticLightsOn(this.debugSettings.analyticLightsContribution)
        this.setToneMappingParameters(this.debugSettings.toneMappingType, this.debugSettings.toneMappingExposure)
        this.setGPUframetime(this.debugSettings.enableGPUframetimeStats)
        this.setAoMapIntensity(this.debugSettings.aoMapIntensity)

        if (this.hasRendered) {
            this.setRenderIsDirty()
        }
    }

    setLinesViz(onoff) {
        if (this.debugVizObjects) {
            this.debugVizObjects.forEach((obj) => {
                // eslint-disable-next-line no-param-reassign
                obj.visible = onoff
            })
        }
    }

    setLightsViz(onoff) {
        if (this.debugLightHelpers) {
            this.debugLightHelpers.forEach((obj) => {
                // eslint-disable-next-line no-param-reassign
                obj.visible = onoff
            })
        }
    }

    setDepthViz(onoff) {
        if (this.occlusionMesh && this.occlusionMesh.material) {
            this.occlusionMesh.material.colorWrite = onoff
        }
    }

    setSegmenationViz(onoff) {
        if (this.sceneBack && this.debugSegmentsMesh) {
            this.debugSegmentsMesh.visible = onoff
        }
    }

    setNormalsViz(onoff) {
        if (this.sceneFront) {
            this.sceneFront.traverse((node) => {
                if (node instanceof THREE.Mesh) {
                    if (onoff && node.material.normalMap) {
                        const oldmat = node.material
                        // eslint-disable-next-line no-param-reassign
                        node.material = new THREE.MeshNormalMaterial({ normalMap: node.material.normalMap })
                        // eslint-disable-next-line no-param-reassign
                        node.material.oldmat = oldmat
                    } else if (node.material.oldmat) {
                        // eslint-disable-next-line no-param-reassign
                        node.material = node.material.oldmat
                    }
                }
            })
        }
    }

    setfrontSideShadows(onoff) {
        if (!this.sceneFront) {
            return
        }

        this.sceneFront.traverse(obj => {
            if (obj instanceof THREE.Mesh && obj.material)
                // eslint-disable-next-line no-param-reassign
                obj.material.shadowSide = onoff ? THREE.FrontSide : null
        })
    }

    setEnvironmentRotation(axis) {
        if (!this.sceneFront) {
            return
        }
        const rotation = [
            [0, 0, 1, 0, 1, 0, 1, 0, 0],    // +Z
            [-1, 0, 0, 0, 1, 0, 0, 0, 1],   // -X
            [0, 0, 1, 0, 1, 0, -1, 0, 0],   // -Z 
            [1, 0, 0, 0, 1, 0, 0, 0, 1],    // +X
        ][axis]
        this.sceneFront.traverse((child) => {
            if (child.isMesh
                && child.material
                && child.material.isMeshStandardMaterial) {
                // eslint-disable-next-line no-param-reassign
                child.material.envMapRotation = new THREE.Matrix3().set(...rotation).transpose()
            }
        })
    }

    setEnvironmentIntensity(intensity) {
        if (!this.sceneFront) {
            return
        }
        this.sceneFront.traverse((child) => {
            if (child.isMesh
                && child.material
                && child.material.isMeshStandardMaterial) {
                // eslint-disable-next-line no-param-reassign
                child.material.envMapIntensity = child.material.originalEnvMapIntensity * intensity
            }
        })
    }

    setAoMapIntensity(intensity) {
        if (!this.sceneFront) {
            return
        }
        this.sceneFront.traverse((child) => {
            if (child.isMesh
                && child.material
                && child.material.isMeshStandardMaterial) {
                // eslint-disable-next-line no-param-reassign
                child.material.aoMapIntensity = intensity
            }
        })
    }

    setLightsIntensity(pointLightIntensities, ambientLightIntensity, directionalLightIntensities) {
        if (!this.sceneFront) {
            return
        }

        this.pointLights.forEach((light, index) => {
            // eslint-disable-next-line no-param-reassign
            light.intensity = light.originalIntensity * pointLightIntensities[index]
        })
        if (this.ambientLight) {
            this.ambientLight.intensity = this.ambientLight.originalIntensity * ambientLightIntensity
        }
        this.directionalLights.forEach((light, index) => {
            // eslint-disable-next-line no-param-reassign
            light.intensity = light.originalIntensity * directionalLightIntensities[index]
        })
    }

    setAnalyticLightsOn(shouldBeOn) {
        if (!this.sceneFront) {
            return
        }
        this.sceneFront.traverse((child) => {
            if (child.isLight && child.originalColor) {
                // eslint-disable-next-line no-param-reassign
                child.color = shouldBeOn
                    ? child.originalColor
                    : new THREE.Color(0.0, 0.0, 0.0)
            }
        })
    }

    setToneMappingParameters(type, exposure) {
        if (!this.ThreeWGLrenderer) {
            return
        }
        const prevToneMappingType = this.ThreeWGLrenderer.toneMapping
        this.ThreeWGLrenderer.toneMapping = type
        this.ThreeWGLrenderer.toneMappingExposure = exposure
        if (this.sceneFront && prevToneMappingType !== type) {
            this.sceneFront.traverse((child) => {
                if (child.material) {
                    // eslint-disable-next-line no-param-reassign
                    child.material.needsUpdate = true
                }
            })
        }
    }

    setAntiAliasing(desiredType) {
        if (!this.composer || !this.ThreeWGLrenderer) {
            return
        }
        let type = desiredType
        if (type === AA_TAA) {
            const floatRTresult = this.textureHelper.testFloatRT()
            if (!floatRTresult || !this.ThreeWGLrenderer.capabilities.isWebGL2) {
                type = AA_FXAA
            }
        }
        if (this.aaType === type) {
            return
        }

        this.aaType = type
        if (this.antiAliasPass) {
            this.composer.removePass(this.antiAliasPass)
            if (this.antiAliasPass.dispose) {
                this.antiAliasPass.dispose.apply(this.antiAliasPass)
            }
            this.antiAliasPass = null
        }

        // might be turned off for TAA
        this.renderPass.enabled = true
        this.outlinePass.enabled = this.showOutlines

        switch (type) {
            case AA_FXAA:
                this.antiAliasPass = new ShaderPass(FXAAShader)
                this.antiAliasPass.uniforms.resolution.value.set(1 / this.canvas.width, 1 / this.canvas.height)
                this.composer.addPass(this.antiAliasPass)
                break
            case AA_TAA: {
                this.renderPass.enabled = false
                this.outlinePass.enabled = false
                const renderFunction = (renderer, writeBuffer, readBuffer) => {
                    this.drawTheThings()
                    if (this.showOutlines) {
                        this.outlinePass.render(renderer, writeBuffer, readBuffer)
                    }
                }
                this.antiAliasPass = new CustomTAARenderPass(this.camera, renderFunction)
                this.composer.addPass(this.antiAliasPass)
                this.composer.addPass(new ShaderPass(CopyShader))
                break
            }
            default:
                break
        }
        this.setRenderIsDirty()
    }

    onChildUpdate(oneTimeRenderCallback = null) {
        if (oneTimeRenderCallback) {
            this.oneTimeRenderCallbacks.push(oneTimeRenderCallback)
        }
        this.setRenderIsDirty()
    }

    onNextRender(cb, skippedFrames = 0) {
        // Run the callbacks after the specified render
        if (skippedFrames > 0) {
            this.scheduledRenderCallbacks.push({ cb, skippedFrames })
        } else {
            this.oneTimeRenderCallbacks.push(cb)
        }
        this.setRenderIsDirty()
    }

    jitterAllLights(fraction) {
        [...this.directionalLights, ...this.pointLights].forEach((light) => {
            if (light.ext_centerPosition) {
                const p = light.ext_centerPosition
                if (light.ext_volume) {
                    let maxis = 0
                    let longest = 0
                    let tmp = 0
                    const lv = light.ext_volume
                    const deltaX = new THREE.Vector3(lv[0].x, lv[0].y, lv[0].z)
                    const deltaY = new THREE.Vector3(lv[1].x, lv[1].y, lv[1].z)
                    const deltaZ = new THREE.Vector3(lv[2].x, lv[2].y, lv[2].z)
                    longest = deltaX.length()
                    tmp = deltaY.length()
                    if (tmp > longest) {
                        longest = tmp
                        maxis = 1
                    }
                    tmp = deltaZ.length()
                    if (tmp > longest) {
                        maxis = 2
                    }

                    if (maxis === 0) {
                        deltaX.multiplyScalar(fraction - 0.5)
                    } else {
                        deltaX.multiplyScalar(Math.random() - 0.5)
                    }
                    if (maxis === 1) {
                        deltaY.multiplyScalar(fraction - 0.5)
                    } else {
                        deltaY.multiplyScalar(Math.random() - 0.5)
                    }
                    if (maxis === 2) {
                        deltaZ.multiplyScalar(fraction - 0.5)
                    } else {
                        deltaZ.multiplyScalar(Math.random() - 0.5)
                    }

                    light.position.set(
                        p.x + deltaX.x + deltaY.x + deltaZ.x,
                        p.y + deltaX.y + deltaY.y + deltaZ.y,
                        p.z + deltaX.z + deltaY.z + deltaZ.z)

                    if (light.ext_debugviz) {
                        light.ext_debugviz.position.set(
                            p.x + deltaX.x + deltaY.x + deltaZ.x,
                            p.y + deltaX.y + deltaY.y + deltaZ.y,
                            p.z + deltaX.z + deltaY.z + deltaZ.z)
                    }
                } else {
                    const dx = light.ext_size * (Math.random() - 0.5)
                    const dy = light.ext_size * (Math.random() - 0.5)
                    const dz = light.ext_size * (Math.random() - 0.5)
                    light.position.set(p.x + dx, p.y + dy, p.z + dz)

                    if (light.ext_debugviz) {
                        light.ext_debugviz.position.set(p.x + dx, p.y + dy, p.z + dz)
                    }
                }
                // eslint-disable-next-line no-param-reassign
                // light.needsUpdate = true
            }
        })
    }

    selectLightShadow(count) {
        let i = 0;
        [...this.directionalLights, ...this.pointLights].forEach((light) => {
            if (light) {
                if (i === count) {
                    // eslint-disable-next-line no-param-reassign
                    light.visible = true
                    if (this.shadowfloor) {
                        WallspiceWebgl.setOpacity(this.shadowfloor,
                            light.intensity * (this.directionalLights.length + this.pointLights.length))
                    }
                    // eslint-disable-next-line no-param-reassign
                    light.distance = 0.001
                } else {
                    // eslint-disable-next-line no-param-reassign
                    light.visible = false
                    // eslint-disable-next-line no-param-reassign
                    light.distance = 0.0
                }
                // eslint-disable-next-line no-param-reassign
                // light.needsUpdate = true
                i += 1
            }
        })
    }

    setBlackFloor(state) {
        // this.shadowfloorBlack.visible = true
        if (this.shadowfloor && this.shadowfloorBlack) {
            if (state) {
                this.shadowfloor.visible = false
                this.shadowfloorBlack.visible = true
            } else {
                this.shadowfloor.visible = true
                this.shadowfloorBlack.visible = false
            }
        }
    }

    allLightShadow() {
        let i = 0;
        [...this.directionalLights, ...this.pointLights].forEach((light) => {
            if (light) {
                if (i === 0) {
                    if (this.shadowfloor) {
                        WallspiceWebgl.setOpacity(this.shadowfloor, 1.0)
                    }
                }
                // eslint-disable-next-line no-param-reassign
                light.visible = true
                // eslint-disable-next-line no-param-reassign
                light.distance = 0.0
                // light.needsUpdate = true
                i += 1
            }
        })
    }

    allLightOneShadow() {
        let i = 0;
        [...this.directionalLights, ...this.pointLights].forEach((light) => {
            if (light) {
                if (i === 0) {
                    if (this.shadowfloor) {
                        WallspiceWebgl.setOpacity(this.shadowfloor, light.intensity)
                    }
                    // eslint-disable-next-line no-param-reassign
                    light.castShadow = true
                } else {
                    // eslint-disable-next-line no-param-reassign
                    light.castShadow = false
                }
                // eslint-disable-next-line no-param-reassign
                light.visible = true
                // eslint-disable-next-line no-param-reassign
                light.distance = 0.0
                // light.needsUpdate = true
                i += 1
            }
        })
    }

    resetLights() {
        let i = 0;
        [...this.directionalLights, ...this.pointLights].forEach((light) => {
            if (light) {
                if (light.ext_centerPosition) {
                    // eslint-disable-next-line no-param-reassign
                    light.position.set(light.ext_centerPosition.x,
                        light.ext_centerPosition.y, light.ext_centerPosition.z)

                    if (light.debugviz) {
                        light.debugviz.position.set(
                            light.ext_centerPosition.x, light.ext_centerPosition.y, light.ext_centerPosition.z)
                    }
                }
                // eslint-disable-next-line no-param-reassign
                // light.castShadow = true
                if (i === 0) {
                    // eslint-disable-next-line no-param-reassign
                    light.visible = true
                    // eslint-disable-next-line no-param-reassign
                    light.distance = 0.0
                    if (this.shadowfloor) {
                        WallspiceWebgl.setOpacity(this.shadowfloor, light.intensity)
                    }
                } else {
                    // eslint-disable-next-line no-param-reassign
                    light.visible = false
                }
                // eslint-disable-next-line no-param-reassign
                // light.needsUpdate = true
            }
            i += 1
        })
    }

    // eslint-disable-next-line class-methods-use-this
    gammacorrect(color) {
        return 255.0 * ((color / 255.0) ** 2.2)
    }

    setRenderIsDirty() {
        this.renderIsDirty = true
        if (this.antiAliasPass && this.antiAliasPass.accumulate !== undefined) {
            this.antiAliasPass.accumulate = false
        }
    }

    isFrameNeedsRender() {
        return this.renderIsDirty || (this.antiAliasPass && this.antiAliasPass.accumulate 
            && this.antiAliasPass.accumulateIndex < 32)
    }

    handleGlError() {
        if (!this.ThreeWGLrenderer) {
            return
        }
        const context = this.ThreeWGLrenderer.getContext()
        if (!context) {
            return
        }
        const err = context.getError()
        if (err !== 0) {
            const composerRtSize = {
                width: 0,
                height: 0,
            }
            if (this.composer && this.composer.readBuffer) {
                composerRtSize.width = this.composer.readBuffer.width
                composerRtSize.height = this.composer.readBuffer.height
            }
            const usingTAA = this.antiAliasPass && this.antiAliasPass.isCustomTAARenderPass
            const taaSampleRtSize = {
                width: 0,
                height: 0
            }
            if (usingTAA) {
                taaSampleRtSize.width = (this.antiAliasPass.sampleRenderTarget && this.antiAliasPass.sampleRenderTarget.width) || 0
                taaSampleRtSize.height = (this.antiAliasPass.sampleRenderTarget && this.antiAliasPass.sampleRenderTarget.height) || 0
            }
            Sentry.captureEvent({
                message: 'GL Error',
                breadcrumbs: [
                    {
                        type: 'GL error data',
                        category: SENTRY_BREADCRUMB_CATEGORY,
                        data: {
                            errorCode: err,
                            hasWallData: !!this.wallTexture,
                            hasComposer: !!this.composer,
                            composerRtSize,
                            usingTAA,
                            taaSampleRtSize,
                        }
                    },
                ]
            })
        }
    }

    animate() {
        this.animationFrameId = requestAnimationFrame(this.animate)
        if (this.renderWasDirty) {
            this.renderWasDirty = false
            if (this.activeFrameMetric) {
                this.activeFrameMetric.intervalStop()
                this.activeFrameMetric = null
            }
        }
        if (this.isFrameNeedsRender()) {
            const itemsAreLoading = this.loadingItems > 0
            if (this.settings.collectFrameMetric && !this.magicWindowWasLoading) {
                this.activeFrameMetric = (itemsAreLoading || this.itemsWereLoading)
                    ? this.frameMetricLoading
                    : this.frameMetric
                this.activeFrameMetric.intervalStart()
            }
            this.render()
            this.renderWasDirty = true
            // allow next frame capture as it is still affected by loading
            this.magicWindowWasLoading = !this.settings.collectFrameMetric
            this.itemsWereLoading = itemsAreLoading
        }
        this.renderIsDirty = !this.hasRendered
        if (this.hasRendered &&
            this.antiAliasPass &&
            this.antiAliasPass.accumulate === false) {
            this.antiAliasPass.accumulate = true
        }
        this.handleGlError()
    }

    render() {
        if (!this.shouldRender) {
            return
        }

        if (!this.debugSettings.useMultiShadow) {
            this.allLightOneShadow()
        }

        if (this.composer) {
            this.composer.render()
        } else {
            this.drawTheThings()
        }

        // begin beautyshot timeout
        if (this.debugSettings.useFancyShadows) {
            // TODO
        }

        if (this.scheduledRenderCallbacks.length > 0) {
            let i = this.scheduledRenderCallbacks.length
            while (i--) {
                const scheduled = this.scheduledRenderCallbacks[i]
                if (scheduled.skippedFrames === 0) {
                    this.oneTimeRenderCallbacks.push(scheduled.cb)
                    this.scheduledRenderCallbacks.splice(i, 1)
                } else {
                    scheduled.skippedFrames -= 1
                }
            }
        }

        if (this.oneTimeRenderCallbacks.length > 0) {
            // YP: TODO: these callbacks will usually dispatch redux action(s). and may start a complex
            // chain of synchronous calls, e.g. instantiating new items via redux, etc, while still in the render() function
            // perhaps even assigning new oneTimeRenderCallbacks which will be cleared right away here, 
            // or clearing render flag without rendering, etc. causing hard to trace bugs.
            //
            // MO: Because callbacks can add new callbacks, loop and run the first in the queue
            // until the queue is empty
            while (this.oneTimeRenderCallbacks[0]) {
                this.oneTimeRenderCallbacks[0]()
                this.oneTimeRenderCallbacks.shift()
            }
        }

        if (this.fullyInitialized && !this.hasRendered) {
            this.hasRendered = true
            this.onFirstRender()
        }
    }

    getHistogramStats() {
        const totalSceneTriangles = this.ThreeWGLrenderer
            ? this.ThreeWGLrenderer.info.render.triangles
            : 0

        const stats = {
            totalLoadedItems: this.loadedItems,
            totalLoadedSize: this.loadedTotalSize,
            totalSceneTriangles,
        }
        stats.totalFrames = this.frameMetric.valuesCount + this.frameMetricLoading.valuesCount
        this.frameMetric.bins.forEach((bin, idx) => {
            stats[bin.title] = this.frameMetric.histogram[idx]
        })
        this.frameMetricLoading.bins.forEach((bin, idx) => {
            stats[bin.title] = this.frameMetricLoading.histogram[idx]
        })
        stats.glTextureMemory = this.glMemoryRecorder ? this.glMemoryRecorder.currentTextureMemory : 0
        stats.glBufferMemory = this.glMemoryRecorder ? this.glMemoryRecorder.currentBufferMemory : 0

        // memory is supported only in chrome
        if (performance.memory) {
            const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = performance.memory

            stats.jsHeapSizeLimit = jsHeapSizeLimit
            stats.totalJSHeapSize = totalJSHeapSize
            stats.usedJSHeapSize = usedJSHeapSize
        }

        // not all browsers may support gpu frametimes
        if (this.gpuFrametime.enable) {
            const nanotomilli = (nano) => nano / 1e6
            stats.gpuFrametimeMin = nanotomilli(this.gpuFrametime.mintime)
            stats.gpuFrametimeMax = nanotomilli(this.gpuFrametime.maxtime)
            stats.gpuFrametimeCumulativeAvg = nanotomilli(this.gpuFrametime.cumulativeAvg)
            stats.gpuFrametimeNumSamples = this.gpuFrametime.numSamples
        }

        return stats
    }

    getRoomGlbStats() {
        let totalModelsLoadSizeMB = 0.0
        let modelsCount = 0
        this.renderItems.forEach((item) => {
            totalModelsLoadSizeMB += item.metrics.MbFileSizeTransferred
            modelsCount += 1
        })
        return {
            totalModelsLoadSizeMB,
            modelsCount,
        }
    }

    getRoomAdditionalStats() {
        const sceneAdditionalDataSizeMB = this.additionalDataLoadSize / 1024 / 1024
        return {
            sceneAdditionalDataSizeMB,
        }
    }

    /* eslint-disable no-param-reassign */
    renderFootprints() {
        if (!this.footprintMaterial) {
            return
        }
        const prevOverrideMtl = this.sceneFront.overrideMaterial
        this.sceneFront.overrideMaterial = this.footprintMaterial
        this.sceneFront.children.forEach((child) => {
            if (child.isFurniture) {
                child.traverse((furnitureChild) => {
                    if (furnitureChild.isMesh) {
                        if (!furnitureChild.prevOnBeforeRender) {
                            furnitureChild.prevOnBeforeRender = furnitureChild.onBeforeRender
                        }
                        furnitureChild.onBeforeRender = (...args) => {
                            FootprintMaterial.refreshUniforms(...args, furnitureChild)
                        }

                        if (!furnitureChild.prevOnAfterRender) {
                            furnitureChild.prevOnAfterRender = furnitureChild.onAfterRender
                        }
                        furnitureChild.onAfterRender = () => {
                            furnitureChild.onBeforeRender = furnitureChild.prevOnBeforeRender
                            furnitureChild.onAfterRender = furnitureChild.prevOnAfterRender
                            furnitureChild.prevOnBeforeRender = null
                            furnitureChild.prevOnAfterRender = null
                        }
                    }
                })
            } else {
                child.wasVisible = child.visible
                child.visible = false
            }
        })

        this.ThreeWGLrenderer.render(this.sceneFront, this.camera)

        this.sceneFront.overrideMaterial = prevOverrideMtl
        this.sceneFront.children.forEach((child) => {
            if (!child.isFurniture) {
                child.visible = child.wasVisible
            }
        })
    }
    /* eslint-enable no-param-reassign */

    checkQueryResults(gl, queryobj) {
        const nanotomilli = (nano) => nano / 1e6
        const cumulativeAvg = (newSample, lastAvg, currNumSamples) => (newSample + currNumSamples * lastAvg) / (currNumSamples + 1)
        const available = gl.getQueryParameter(queryobj, gl.QUERY_RESULT_AVAILABLE);
        if (available) {
            // elapsedResult is in nanoseconds
            const elapsedResult = gl.getQueryParameter(queryobj, gl.QUERY_RESULT);
            if (elapsedResult > 0) {
                this.gpuFrametime.enableQuery = true
                this.gpuFrametime.lastQueryResultMS = nanotomilli(elapsedResult)
                this.gpuFrametime.cumulativeAvg = cumulativeAvg(elapsedResult, this.gpuFrametime.cumulativeAvg, this.gpuFrametime.numSamples)
                this.gpuFrametime.numSamples += 1
                this.gpuFrametime.mintime = Math.min(this.gpuFrametime.mintime, elapsedResult)
                this.gpuFrametime.maxtime = Math.max(this.gpuFrametime.maxtime, elapsedResult)
                const log = `\nGPU frame times:
                \nnanosec: ${elapsedResult}
                \nmillisec: ${this.gpuFrametime.lastQueryResultMS}
                \nmintime: ${nanotomilli(this.gpuFrametime.mintime)}
                \nmaxtime: ${nanotomilli(this.gpuFrametime.maxtime)}
                \ncumulative Avg: ${nanotomilli(this.gpuFrametime.cumulativeAvg)}`
                if (this.gpuFrametime.enableStats) {
                    this.gpuFrametime.statsTextDom.nodeValue = log
                }
            }
        }
    }

    drawTheThings() {
        if (!this.ThreeWGLrenderer || !this.camera) {
            return
        }
        /* According to the specification, one cannot query GPU frametime until the previous query 
        is complete and retrieved to the host. Hence, query begin and end are guarded */
        if (this.gpuFrametime.enable) {
            const gl = this.ThreeWGLrenderer.getContext()
            const ext = this.gpuFrametime.ext
            if (this.gpuFrametime.enableStats) {
                this.gpuFrametime.stats.begin()
            }

            if (this.gpuFrametime.enableQuery) {
                gl.beginQuery(ext.TIME_ELAPSED_EXT, this.gpuFrametime.queryObj);
            }
        }
        const rendersCount = this.debugSettings.imitateLowPerformance ? 25 : 1
        for (let i = 0; i < rendersCount; i += 1) {
            this.ThreeWGLrenderer.clear(true, true, true)
            if (this.sceneBack) {
                this.ThreeWGLrenderer.render(this.sceneBack, this.camera)
            }
            if (this.mid && this.sceneMid && !this.settings.magicEraserMode) {
                this.ThreeWGLrenderer.render(this.sceneMid, this.camera)
            }
            this.ThreeWGLrenderer.clearDepth()
            if (!this.settings.magicEraserMode) {
                this.renderFootprints()
            }
            if (this.sceneOcclusion) {
                this.ThreeWGLrenderer.render(this.sceneOcclusion, this.camera)
            }
            if (!this.settings.magicEraserMode) {
                this.ThreeWGLrenderer.render(this.sceneFront, this.camera)
            }
            if (this.hasStaticStackableSurfaces && !this.settings.magicEraserMode) {
                // Note stackables on static surfaces are split into a separate Threejs native layer
                // for customized shadow interaction with the surfaces/floor, so a different render call is required
                this.camera.layers.set(STATIC_STACKABLES_THREE_NATIVE_LAYER)
                this.ThreeWGLrenderer.render(this.sceneFront, this.camera)
                this.camera.layers.set(DEFAULT_THREE_NATIVE_LAYER)
            }
        }
        if (this.gpuFrametime.enable) {
            const gl = this.ThreeWGLrenderer.getContext()
            const ext = this.gpuFrametime.ext
            if (this.gpuFrametime.enableQuery) {
                gl.endQuery(ext.TIME_ELAPSED_EXT)
                this.gpuFrametime.enableQuery = false
            }
            if (this.gpuFrametime.enableStats) {
                this.gpuFrametime.stats.end()
                this.gpuFrametime.queryPanel.update(this.gpuFrametime.lastQueryResultMS, 400)
            }

            window.requestAnimationFrame(() => {
                this.checkQueryResults(gl, this.gpuFrametime.queryObj)
            })
        }

        this.ThreeWGLrenderer.getContext().flush()
        this.ThreeWGLrenderer.getContext().finish()
    }

    takeRoomScreenshot(onSuccess, onError) {
        /*
            This function takes a high quality screenshot of the room with no composer effects
            If the current exact preview is needed; use the generateThumbnailFromPreview directly
        */ 

        // Set the renderer's state suitable for taking a screenshot
        const forceTAA = this.debugSettings.useTAAforScreenshots
        const currentAA = this.aaType
        this.enableOutline(false)
        this.setAntiAliasing(forceTAA ? AA_TAA : AA_FXAA)

        // Take screenshot and restore the earlier renderer state
        this.onNextRender(() => {
            generateThumbnailFromPreview().then(
                (baseImageData) => {
                    this.enableOutline()
                    this.setAntiAliasing(currentAA)
                    if (onSuccess) onSuccess(baseImageData)
                },
                (error) => {
                    this.enableOutline()
                    this.setAntiAliasing(currentAA)
                    if (onError) onError(error)
                },
            )
        }, forceTAA ? 32 : 0)
    }

    cleanup() {
        cancelAnimationFrame(this.animationFrameId)
        this.activeRenderer = false

        this.shouldRender = false
        if (this.composer) {
            this.composer.renderTarget1.dispose()
            this.composer.renderTarget2.dispose()
        }
        if (this.outlinePass) {
            this.outlinePass.dispose()
        }
        if (this.renderPass) {
            this.renderPass.renderFunc = null
        }
        Renderer.disposeMesh(this.highlightMesh)
        Renderer.disposeMesh(this.occlusionMesh)
        Renderer.disposeMesh(this.segmentsMesh)
        Renderer.disposeMesh(this.emptyOcclusionMesh)
        Renderer.disposeMesh(this.foregroundMesh)

        // Loading manager can retain Renderer's "this" somewhere, causing memory leak
        THREE.DefaultLoadingManager.onLoad = undefined
        if (this.ThreeWGLrenderer) {
            this.ThreeWGLrenderer.dispose()
        }
        if (this.renderItems) {
            this.renderItems.clear()
        }
        if (this.furnitureManager) {
            this.furnitureManager.dispose()
        }
        if (this.glMemoryRecorder) {
            this.glMemoryRecorder.stop()
        }

        if (this.gpuFrametime && this.gpuFrametime.enable) {
            const gl = this.ThreeWGLrenderer.getContext()
            gl.deleteQuery(this.gpuFrametime.queryObj)
            this.gpuFrametime.queryObj = null
            this.gpuFrametime.enable = false
        }

        rendererInstance = null
    }

    static disposeMesh(mesh) {
        if (!mesh) {
            return
        }
        if (mesh.material) {
            mesh.material.dispose()
        }
        if (mesh.geometry) {
            mesh.geometry.dispose()
        }
    }

    enableOutline(enable = true) {
        const { disableOutline } = this.settings
        const onoff = disableOutline ? false : enable

        this.showOutlines = onoff

        if (this.outlinePass) {
            switch (this.aaType) {
                case AA_FXAA: {
                    this.outlinePass.enabled = onoff
                    break
                }
                case AA_TAA: {
                    // With TAA, outlinePass is rendered "manullay" if showOutlines is true
                    // and internal .enabled is always false
                    this.outlinePass.enabled = false
                    break
                }
                default:
                    break
            }
        }

        this.setRenderIsDirty()
    }

    addObjectToOutline(object) {
        if (!object || !this.outlinePass) {
            return
        }
        const outlineArray = this.outlinePass.selectedObjects
        const index = outlineArray.indexOf(object)
        if (index === -1) {
            outlineArray.push(object)
            this.setRenderIsDirty()
        }
    }

    removeObjectFromOutline(object) {
        if (!object || !this.outlinePass) {
            return
        }
        const outlineArray = this.outlinePass.selectedObjects
        const index = outlineArray.indexOf(object)
        if (index > -1) {
            outlineArray.splice(index, 1)
            this.setRenderIsDirty()
        }
    }

    setThickOutline(isThick) {
        if (!this.outlinePass) {
            return
        }
        this.outlinePass.edgeThickness = isThick ? EDGE_THICKNESS_THICK : EDGE_THICKNESS_THIN
        this.outlinePass.edgeStrength = isThick ? EDGE_STRENGTH_THICK : EDGE_STRENGTH_THIN
        this.setRenderIsDirty()
    }

    static checkWebPSupport() {
        const tmpCanvas = document.createElement('canvas')
        if (tmpCanvas.getContext && tmpCanvas.getContext('2d')) {
            return tmpCanvas.toDataURL('image/webp').indexOf('data:image/webp') === 0
        }
        return false
    }

    static getFileSize(url) {
        if (!url) {
            return Promise.resolve(0.0)
        }
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            xhr.open("HEAD", url, true)
            xhr.onreadystatechange = () => {
                if (xhr.readyState === xhr.DONE) {
                    const size = parseInt(xhr.getResponseHeader("Content-Length"), 10)
                    resolve(size)
                }
            }
            xhr.onerror = reject
            xhr.send()
        })
    }
}

export default Renderer
